import { useMutation, useQuery, useQueryClient } from 'react-query';
import { queryCreate, queryGet, queryUpdate } from './fetch';
import { useCallback } from 'react';
import { NewQuota } from './quota';

export enum CriterionBehavior {
  QUALIFY = 'QUALIFY',
  DISQUALIFY = 'DISQUALIFY',
  IGNORE = 'IGNORE',
  TO_BE_VALIDATED = 'IGNORE',
}

export enum RecruitmentStatus {
  PAUSED = 'PAUSED',
  LAUNCHED = 'LAUNCHED',
  DRAFT = 'DRAFT',
  COMPLETED = 'COMPLETED',
}

export enum RecruitmentBy {
  TANDEMZ = 'TANDEMZ',
  CLIENT = 'CLIENT',
}

export enum ListValueType {
  KEYWORD = 'KEYWORD',
  TEXT = 'TEXT',
  LOCATION = 'LOCATION',
  AGE_RANGE = 'AGE_RANGE',
  LOCALE = 'LOCALE',
}

export enum SelectionBehavior {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
  NO_SELECTION = 'NO_SELECTION',
}

export interface Range {
  min: number | null;
  max: number | null;
}

export interface Location {
  location: { lon: number; lat: number } | null;
  radius?: number;
  name: string;
  address?: string;
  placeId?: string;
}

export type CriterionListItem = string | number | Range | Location;

export interface QualifyingListItem {
  value: string;
  behavior: CriterionBehavior;
}

export interface Quota {
  item: CriterionListItem;
  value: number;
  ref?: string;
  label?: string;
  confirmed?: number;
  max?: number;
}

export interface QuotaTag {
  fieldRef: string;
  item: string;
  name: string;
  color: string;
}

export interface Criterion {
  id: string;
  fieldRef: string;
  selectionBehavior?: SelectionBehavior;
  screenerCriterion: boolean;

  allowList?: CriterionListItem[] | null;
  denyList: CriterionListItem[];
  tolerateList?: CriterionListItem[] | null;
  automaticlyAcceptedList?: CriterionListItem[];
  listValueType?: ListValueType;
  quotas?: Quota[] | null;
}

export interface CriteriaCountByState {
  [criterionId: string]: {
    [quotaItem: string]: Record<string, number>;
  };
}

export interface ExtendedCriterion {
  fieldLabel: string;
  quotas: NewQuota[] | undefined;
  id: string;
  fieldRef: string;
  color: string;
}

export interface Recruitment {
  id: string;
  organizationId: string;
  sessionId: string;
  screeningFormId?: string;
  criteria: Criterion[];
  automaticValidation: boolean;
  status: RecruitmentStatus;
  countryKeys: string[];
  recruitmentBy: RecruitmentBy;
  allowAlreadyParticipated: boolean;
  allowTechParticipants: boolean;
  participantsRecruited: string[];
  numberOfParticipantRequired: number;
  targetInformation?: string;
}

export const useRecruitment = (id?: string) => {
  return useQuery<Recruitment>(
    ['recruitments', id],
    queryGet(`/v2/recruitments/${id}`),
    {
      enabled: !!id,
    },
  );
};

export const useCreateRecruitment = () => {
  return useMutation<Recruitment, undefined, Partial<Recruitment>>(
    queryCreate(`/v2/recruitments/`),
  );
};

export const useCriteriaCountByState = (recruitmentId?: string) => {
  return useQuery<CriteriaCountByState>(
    ['recruitments', recruitmentId, 'criteria'],
    queryGet(`/v2/recruitments/${recruitmentId}/criteria/grouped-count`),
    {
      enabled: !!recruitmentId,
    },
  );
};

export const useGetApplicationsByCriterionAndChoices = (
  recruitmentId?: string,
) => {
  return useQuery(
    ['recruitments', recruitmentId, 'applications-by-choices'],
    queryGet(
      `/v2/recruitments/${recruitmentId}/criteria/applications-by-choices`,
    ),
    {
      enabled: !!recruitmentId,
    },
  );
};

export const useUpdateCriteria = (recruitmentId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Recruitment, undefined, Criterion[]>(
    queryUpdate(`/v2/recruitments/${recruitmentId}/criteria`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['recruitments', recruitmentId]);
      },
    },
  );
};

export const useUpdateRecruitment = (id?: string) => {
  const queryClient = useQueryClient();
  const mutate = useCallback(
    (recruitment: any) =>
      queryUpdate(`/v2/recruitments/${recruitment.id || id}`)(recruitment),
    [id],
  );

  return useMutation<Recruitment, undefined, any>(mutate, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['recruitments', data.id]);
    },
  });
};

export const useCountryCriterion = (recruitmentId?: string) => {
  const res = useRecruitment(recruitmentId);
  if (!res.data || !res.data.criteria) {
    return {
      ...res,
      data: undefined,
    };
  }

  const countryCriterion = res.data.criteria.find(
    ({ fieldRef }) => fieldRef === 'countryKey',
  );
  if (!countryCriterion) {
    return {
      ...res,
      data: undefined,
    };
  }
  return {
    ...res,
    data: countryCriterion,
  };
};

export const useUpdateCriterion = (recruitmentId?: string) => {
  const { data: recruitment } = useRecruitment(recruitmentId);
  const res = useUpdateCriteria(recruitmentId);

  const updateCriteria = res.mutateAsync;
  const mutateAsync = useCallback(
    (criterion: Criterion) => {
      if (!recruitment || !recruitment.criteria) {
        return;
      }

      let criteria = [...recruitment.criteria];
      if (
        !criterion.allowList &&
        (!criterion.denyList || !criterion.denyList.length) &&
        !criterion.automaticlyAcceptedList
      ) {
        criteria = recruitment.criteria.filter(
          ({ id, fieldRef }) =>
            id !== criterion.id && fieldRef !== criterion.fieldRef,
        );
      } else {
        const index = criteria.findIndex(
          ({ id, fieldRef }) =>
            id === criterion.id || fieldRef === criterion.fieldRef,
        );
        if (index === -1) {
          criteria.push(criterion);
        } else {
          criteria[index] = criterion;
        }
      }
      return updateCriteria(criteria);
    },
    [updateCriteria, recruitment],
  );

  return {
    ...res,
    mutateAsync,
    mutate: mutateAsync,
  };
};
